import { Download } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  FormDatePicker,
  FormTextField,
  GridCardContent,
} from "components/atoms";
import AppModal from "components/atoms/AppModal";
import { AppTable, ReportHeader } from "components/molecules";
import { FormikHelpers, useFormik } from "formik";
import { TableHelper, TimeHelper } from "helpers";
import { usePaginationRequest } from "hooks/useRequest";
import { forEach, isEmpty, isNull, map } from "lodash";
import {
  IAuditTrail,
  IDownloadExcelRequest,
  IGetAuditTrailRequest,
  IPaginateApiResponse,
} from "model";
import moment from "moment";
import { Fragment, createContext, useContext, useState } from "react";
import { useDispatch } from "react-redux";
import { DownloadActions } from "redux/actions";
import { useAppSelector } from "redux/configurations/reduxHooks";
import { ReportApiService } from "services";
import { CURRENT_PAGE_SIZE, EXPORT_FILE_NAME, FIRST_PAGE } from "utils/Const";
import { I18nTranslator, translations } from "utils/i18n";
import XMLViewer from "react-xml-viewer";
import { AUDIT_TRAIL_ACTION } from "common";
import AuditTrailActionCell from "components/atoms/AuditTrailActionCell";

const AuditTrail = () => {
  const dispatch = useDispatch();
  const [request, setRequest] = useState<IGetAuditTrailRequest>({
    code: "",
    user: "",
    fromDate: TimeHelper.startOfWeek("MM-DD-YYYY"),
    toDate: TimeHelper.endOfWeek("MM-DD-YYYY"),
    pageNumber: FIRST_PAGE,
    pageSize: CURRENT_PAGE_SIZE,
  });

  const { loading, result } = usePaginationRequest<
    IGetAuditTrailRequest,
    IAuditTrail[]
  >(request, ReportApiService.getAuditTrail, [], {}, () => {});
  const handleSubmit = (values: IGetAuditTrailRequest) => {
    setRequest(values);
  };

  const handleChangePage = (pageNumber: number) => {
    setRequest({
      ...request,
      pageNumber: pageNumber,
    });
  };

  const _handlePageSizeChange = (pageSize: number) => {
    setRequest({ ...request, pageSize: pageSize });
  };

  const _downloadAuditTrail = () => {
    const params: IDownloadExcelRequest = {
      ...request,
      pageSize: (result as IPaginateApiResponse<IAuditTrail[]>).totalRecords,
      pageNumber: FIRST_PAGE,
      fromDate: request.fromDate,
      toDate: request.toDate,
      fileName: EXPORT_FILE_NAME.auditTrail,
    };
    dispatch(DownloadActions.downloadAuditTrail.request(params));
  };

  return (
    <div
      style={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        height: "100%",
      }}
    >
      <DetailHeader
        request={request}
        onSubmit={handleSubmit}
        downloadAuditTrail={_downloadAuditTrail}
      />
      <TableSection
        loading={loading}
        result={result}
        onChangePage={handleChangePage}
        onPageSizeChange={_handlePageSizeChange}
      />
    </div>
  );
};

const DetailHeader = (props: {
  request: IGetAuditTrailRequest;
  onSubmit(values: IGetAuditTrailRequest): void;
  downloadAuditTrail(): void;
}) => {
  const { request, onSubmit, downloadAuditTrail } = props;
  const downloading = useAppSelector(
    (state) => state.AppConfigReducer.downloading
  );
  const formik = useFormik<any>({
    initialValues: {
      code: "",
      user: "",
      fromDate: moment(request.fromDate, "MM-DD-YYYY"),
      toDate: moment(request.toDate, "MM-DD-YYYY"),
    },
    onSubmit: (values: any, { validateForm }: FormikHelpers<any>) => {
      validateForm().then(() => {
        onSubmit({
          code: values.code,
          user: values.user,
          fromDate: TimeHelper.formatMomentToDate(
            values.fromDate,
            "MM-DD-YYYY"
          ),
          toDate: TimeHelper.formatMomentToDate(values.toDate, "MM-DD-YYYY"),
          pageNumber: FIRST_PAGE,
          pageSize: CURRENT_PAGE_SIZE,
        });
      });
    },
  });

  return (
    <ReportHeader
      title={I18nTranslator(translations.routes.auditTrail)}
      formik={formik}
      loading={false}
      renderButtons={
        <Stack direction={"row"} spacing={2}>
          <LoadingButton
            startIcon={<Download />}
            loading={downloading}
            variant="contained"
            onClick={downloadAuditTrail}
          >
            {I18nTranslator(translations.button.exportDocs)}
          </LoadingButton>
        </Stack>
      }
    >
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={2} item>
          <FormDatePicker
            id="fromDate"
            label={I18nTranslator(translations.fromDate)}
            defaultValue={TimeHelper.formatDate(formik.values.deliveryDate)}
          />
        </Grid>
        <Grid xs={2} item>
          <FormDatePicker
            id="toDate"
            label={I18nTranslator(translations.toDate)}
            defaultValue={TimeHelper.formatDate(formik.values.deliveryDate)}
          />
        </Grid>
        <Grid xs={4} item>
          <FormTextField
            id="code"
            label={I18nTranslator(translations.report.code)}
            placeholder={I18nTranslator(translations.placeholder.code)}
          />
        </Grid>
        <Grid xs={4} item>
          <FormTextField
            id="user"
            label={I18nTranslator(translations.report.executor)}
            placeholder={I18nTranslator(translations.placeholder.executor)}
          />
        </Grid>
      </Grid>
    </ReportHeader>
  );
};

const TableSection = (props: {
  loading: boolean;
  result: IPaginateApiResponse<IAuditTrail[]>;
  onChangePage(pageNumber: number): void;
  onPageSizeChange(pageSize: number): void;
}) => {
  const { loading, result, onChangePage, onPageSizeChange } = props;
  const [oldData, setOldData] = useState<string>("");
  const [newData, setNewData] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);

  const COLUMNS: GridColDef[] = [
    TableHelper.renderSTT(),
    TableHelper.createCell({
      field: "dateTime",
      headerName: I18nTranslator(translations.report.time),
      size: "small",
      align: "left",
      sortable: true,
      renderCell: ({ value }) => {
        return (
          <Typography>
            {TimeHelper.formatDate(value, "DD/MM/YYYY HH:mm")}
          </Typography>
        );
      },
    }),
    TableHelper.createCell({
      field: "user",
      headerName: I18nTranslator(translations.report.executor),
      size: "medium",
      align: "left",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "siteMap",
      headerName: I18nTranslator(translations.tracking.feature),
      size: "small",
      align: "left",
      sortable: true,
      renderCell: ({ row }) => {
        return <Typography>{row.siteMap || row.method}</Typography>;
      },
    }),
    TableHelper.createCell({
      field: "action",
      headerName: I18nTranslator(translations.report.action),
      size: "medium",
      align: "left",
      sortable: true,
      renderCell: ({ value }) => {
        return <AuditTrailActionCell value={value} />;
      },
    }),

    TableHelper.createCell({
      field: "description",
      headerName: I18nTranslator(translations.report.note),
      size: "full",
      align: "left",
    }),
  ];

  return (
    <Fragment>
      <AppTable<any>
        loading={loading}
        data={result}
        columns={COLUMNS}
        actionColumnProps={{
          hideEdit: true,
        }}
        onSearch={(searchValue: string) => {}}
        searchPlaceholder={I18nTranslator(
          translations.placeholder.searchReceiveDocument
        )}
        onChangePage={onChangePage}
        hideHeader
        removeActionColumn
        defaultHeaderTable={180}
        hideSearch
        onPageSizeChange={onPageSizeChange}
        onCellClick={(params) => {
          setOldData(params.row.oldData);
          setNewData(params.row.newData);

          setOpen(true);
        }}
      />
      <AppModal
        open={open}
        title="Chi tiết hoạt động"
        onOk={() => {}}
        onCancel={() => setOpen(false)}
        hiddenBtnOK
        sx={{
          "& .MuiDialog-container": {
            "& .MuiPaper-root": {
              width: "100%",
              maxWidth: isNull(oldData) || isNull(newData) ? "600px" : "1000px", // Set your width here
            },
          },
        }}
      >
        <div
          style={{
            maxHeight: 600,
            height: 500,
            width: "100%",
            display: "flex",
            flexDirection: "row",
          }}
        >
          {oldData && (
            <div style={{ width: 600 }}>
              <Typography fontWeight={500} fontSize={20}>
                Dữ liệu cũ
              </Typography>
              <XMLViewer
                xml={oldData}
                theme={{
                  overflowBreak: true,
                }}
              />
            </div>
          )}
          <div style={{ width: 10 }} />
          {newData && (
            <div style={{ width: 600 }}>
              <Typography fontWeight={500} fontSize={20}>
                Dữ liệu hiện tại
              </Typography>
              <XMLViewer
                xml={newData}
                theme={{
                  overflowBreak: true,
                }}
              />
            </div>
          )}
        </div>
      </AppModal>
    </Fragment>
  );
};

export default AuditTrail;
